// Substring signatures every Sentry init passes as `ignoreErrors` — aborts are
// control flow, not failures. 'Request was aborted.' is the Anthropic SDK's
// APIUserAbortError, which never sets `name` to 'AbortError' and is captured by
// Sentry's auto-instrumentation at the API call site even when the caller
// handles the abort.
export const ABORT_ERROR_SIGNATURES: string[] = ['AbortError', 'Request was aborted.'];

// Standard JS does not export an `AbortError` class; the runtime convention
// is a `DOMException` (or plain Error) with `name === 'AbortError'`. This
// shim gives us a typed surface alongside a name-based detector so we can
// (a) throw one ourselves, (b) detect ones thrown by `fetch`, the Anthropic
// SDK, Playwright, etc.
export class AbortError extends Error {
  override name = 'AbortError';

  static is(e: unknown): boolean {
    // A browser `fetch` abort rejects with a DOMException, which is NOT an
    // `instanceof Error` in current browser/Node implementations (a WebIDL
    // exception, not the JS Error hierarchy) — matching only Error here
    // silently missed the exact case the doc comment above already
    // documents, letting a real abort rethrow as if it were a genuine
    // failure.
    return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError';
  }

  // Use inside a catch when you want to swallow an abort but let any other
  // error keep propagating:
  //   } catch (e) {
  //     AbortError.rethrowIfNotAbort(e);
  //     // ...record abort status...
  //   }
  static rethrowIfNotAbort(e: unknown): void {
    if (!AbortError.is(e)) throw e;
  }
}

// When the client hangs up before the request body has fully arrived (a
// cancelled upload, a closed tab), Node destroys the request with a bare
// `Error: aborted` carrying `code: 'ECONNRESET'` — no application frames, and
// nothing we can act on. Nobody is left to receive the response either.
export function isClientDisconnect(e: unknown): boolean {
  return (
    AbortError.is(e) ||
    (e instanceof Error && e.message === 'aborted' && (e as NodeJS.ErrnoException).code === 'ECONNRESET')
  );
}
